/** Pure helpers for Mitra Screens row/card reorder. */

export const DEFAULT_ZONE_META = {
  operate: {
    zone: 'operate',
    label_en: 'My Business Desk',
    label_te: 'నా బిజినెస్ డెస్క్',
    label_hi: 'मेरा बिज़नेस डेस्क',
    blurb_en: 'Billing, stock, leads, reports — day-to-day shop ops.',
    sort_order: 0,
  },
  grow: {
    zone: 'grow',
    label_en: 'Connect with Loksy',
    label_te: 'లాక్సీతో కనెక్ట్',
    label_hi: 'लोक्सी से कनेक्ट',
    blurb_en: 'Ads, coupons, leads, shop profile & photos for Loksy Citizen.',
    sort_order: 1,
  },
};

export const DEFAULT_SECTION_ORDER = {
  operate: [
    'billing',
    'accounts',
    'catalog',
    'tools',
    'leads_inbox',
    'reports',
    'students',
    'account_settings',
    'pos_shell',
  ],
  grow: ['partner_services', 'store_profile', 'general'],
};

/** Operate filter chips + feature carousels (excludes Account / POS shell rows). */
export const OPERATE_FILTER_SECTION_KEYS = [
  'billing',
  'accounts',
  'catalog',
  'tools',
  'leads_inbox',
  'reports',
  'students',
];

export const OPERATE_FEATURE_SECTION_KEYS = OPERATE_FILTER_SECTION_KEYS;

export const DEFAULT_SECTION_LABELS = {
  billing: { label_en: 'Bills', label_te: 'బిల్లులు', label_hi: 'बिल' },
  accounts: { label_en: 'Accounts', label_te: 'ఖాతాలు', label_hi: 'खाते' },
  catalog: { label_en: 'Products', label_te: 'ప్రొడక్ట్స్', label_hi: 'प्रोडक्ट्स' },
  tools: { label_en: 'Tools', label_te: 'టూల్స్', label_hi: 'टूल्स' },
  leads_inbox: { label_en: 'Leads', label_te: 'లీడ్స్', label_hi: 'लीड्स' },
  reports: { label_en: 'Reports', label_te: 'రిపోర్టులు', label_hi: 'रिपोर्ट्स' },
  students: { label_en: 'Students', label_te: 'స్టూడెంట్స్', label_hi: 'स्टूडेंट्स' },
  account_settings: {
    label_en: 'Account & Settings',
    label_te: 'Account & Settings',
    label_hi: 'Account & Settings',
  },
  pos_shell: {
    label_en: 'POS shell',
    label_te: 'POS shell',
    label_hi: 'POS shell',
  },
  partner_services: {
    label_en: 'Partner Services & Tools',
    label_te: 'పార్టనర్ సర్వీసెస్',
    label_hi: 'पार्टनर सेवाएं',
  },
  store_profile: {
    label_en: 'Store Profile & Config',
    label_te: 'స్టోర్ ప్రొఫైల్',
    label_hi: 'स्टोर प्रोफ़ाइल',
  },
  general: { label_en: 'General', label_te: 'జనరల్', label_hi: 'जनरल' },
};

export function normalizeLayout(raw) {
  const zones = {
    operate: {
      ...DEFAULT_ZONE_META.operate,
      ...(raw?.zones?.operate || {}),
    },
    grow: {
      ...DEFAULT_ZONE_META.grow,
      ...(raw?.zones?.grow || {}),
    },
  };

  const sections = { operate: [], grow: [] };
  for (const zone of ['operate', 'grow']) {
    const fromApi = Array.isArray(raw?.sections?.[zone])
      ? raw.sections[zone]
      : [];
    const byKey = new Map();
    fromApi.forEach((s, idx) => {
      const key = s.section_key || s.sectionKey;
      if (!key) return;
      const isShellRow =
        zone === 'operate' &&
        (key === 'account_settings' || key === 'pos_shell');
      const filterExplicit =
        s.show_in_filter !== undefined || s.showInFilter !== undefined;
      let showInFilter =
        s.show_in_filter !== false &&
        s.showInFilter !== false &&
        s.show_in_filter !== 0;
      // Account / POS rows must not appear as Operate filter chips by default.
      if (isShellRow && !filterExplicit) showInFilter = false;
      byKey.set(key, {
        zone,
        section_key: key,
        label_en: s.label_en || DEFAULT_SECTION_LABELS[key]?.label_en || key,
        label_te: s.label_te || DEFAULT_SECTION_LABELS[key]?.label_te || '',
        label_hi: s.label_hi || DEFAULT_SECTION_LABELS[key]?.label_hi || '',
        blurb_en: s.blurb_en || null,
        sort_order: s.sort_order != null ? Number(s.sort_order) : idx * 10,
        show_in_filter: showInFilter,
      });
    });
    DEFAULT_SECTION_ORDER[zone].forEach((key, idx) => {
      if (!byKey.has(key)) {
        const inFilter =
          zone !== 'operate' || OPERATE_FILTER_SECTION_KEYS.includes(key);
        byKey.set(key, {
          zone,
          section_key: key,
          label_en: DEFAULT_SECTION_LABELS[key]?.label_en || key,
          label_te: DEFAULT_SECTION_LABELS[key]?.label_te || '',
          label_hi: DEFAULT_SECTION_LABELS[key]?.label_hi || '',
          blurb_en: null,
          sort_order:
            key === 'account_settings'
              ? 100
              : key === 'pos_shell'
                ? 110
                : idx * 10,
          show_in_filter: inFilter,
        });
      }
    });
    sections[zone] = [...byKey.values()].sort(
      (a, b) => (a.sort_order || 0) - (b.sort_order || 0),
    );
  }

  return { zones, sections, tableReady: Boolean(raw?.tableReady) };
}

export const DEFAULT_OPERATE_BLOCKS = [
  { block_key: 'ops_directory', label_en: 'Operations Directory', is_visible: true, sort_order: 0 },
  { block_key: 'filter_chips', label_en: 'Filter chips', is_visible: true, sort_order: 10 },
  { block_key: 'store_overview', label_en: 'Store overview', is_visible: true, sort_order: 20 },
  { block_key: 'feature_sections', label_en: 'Feature rows (Bills, Products…)', is_visible: true, sort_order: 30 },
  { block_key: 'account_settings', label_en: 'Account & Settings', is_visible: true, sort_order: 40 },
  { block_key: 'pos_shift_banner', label_en: 'POS shift banner', is_visible: true, sort_order: 50 },
  { block_key: 'pos_terminal', label_en: 'POS terminal card', is_visible: true, sort_order: 60 },
];

export const DEFAULT_GROW_BLOCKS = [
  { block_key: 'search', label_en: 'Search', is_visible: true, sort_order: 0 },
  { block_key: 'store_header', label_en: 'Store header', is_visible: true, sort_order: 10 },
  { block_key: 'quick_access', label_en: 'Quick access', is_visible: true, sort_order: 20 },
  { block_key: 'feature_sections', label_en: 'Feature grids', is_visible: true, sort_order: 30 },
  { block_key: 'business_info', label_en: 'Business info', is_visible: true, sort_order: 40 },
];

export function normalizeBlocks(raw) {
  const merge = (zone, defaults, fromApi) => {
    const list = Array.isArray(fromApi) ? fromApi : [];
    const byKey = new Map();
    list.forEach((b, idx) => {
      const key = b.block_key || b.blockKey;
      if (!key) return;
      byKey.set(key, {
        zone,
        block_key: key,
        label_en: b.label_en || key,
        label_te: b.label_te || '',
        label_hi: b.label_hi || '',
        is_visible: b.is_visible !== false && b.isVisible !== false,
        sort_order: b.sort_order != null ? Number(b.sort_order) : idx * 10,
      });
    });
    defaults.forEach((def, idx) => {
      if (!byKey.has(def.block_key)) {
        byKey.set(def.block_key, { ...def, zone, label_te: '', label_hi: '' });
      }
    });
    return [...byKey.values()].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  };
  return {
    operate: merge('operate', DEFAULT_OPERATE_BLOCKS, raw?.operate),
    grow: merge('grow', DEFAULT_GROW_BLOCKS, raw?.grow),
    tableReady: Boolean(raw?.tableReady),
  };
}

/** Build ordered section buckets for one zone. */
export function groupCardsBySection(zoneCards, sectionDefs) {
  const order = sectionDefs.map((s) => s.section_key);
  const buckets = new Map(order.map((k) => [k, []]));
  const sorted = [...zoneCards].sort(
    (a, b) => (a.display_order || 0) - (b.display_order || 0),
  );
  sorted.forEach((card) => {
    const key = card.section_key || 'general';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(card);
  });
  // Keep unknown sections at the end
  const extraKeys = [...buckets.keys()].filter((k) => !order.includes(k));
  const keys = [...order, ...extraKeys];
  return keys
    .map((key) => ({
      sectionKey: key,
      cards: buckets.get(key) || [],
    }))
    .filter((row) => row.cards.length > 0 || order.includes(row.sectionKey));
}

/** Reassign contiguous display_order + section_key from board rows. */
export function applyBoardToCards(allCards, zone, boardRows) {
  let order = 0;
  const updates = new Map();
  boardRows.forEach((row) => {
    (row.cards || []).forEach((card) => {
      updates.set(card.card_key, {
        section_key: row.sectionKey,
        display_order: order,
      });
      order += 1;
    });
  });
  return allCards.map((c) => {
    if (c.zone !== zone) return c;
    const u = updates.get(c.card_key);
    if (!u) return c;
    return { ...c, section_key: u.section_key, display_order: u.display_order };
  });
}

export function moveSection(sectionDefs, index, dir) {
  const next = [...sectionDefs];
  const swap = index + dir;
  if (index < 0 || swap < 0 || swap >= next.length) return sectionDefs;
  const tmp = next[index];
  next[index] = next[swap];
  next[swap] = tmp;
  return next.map((s, i) => ({ ...s, sort_order: i * 10 }));
}

export function moveCardInBoard(boardRows, sectionKey, cardKey, dir) {
  const rows = boardRows.map((r) => ({
    ...r,
    cards: [...r.cards],
  }));
  const row = rows.find((r) => r.sectionKey === sectionKey);
  if (!row) return boardRows;
  const idx = row.cards.findIndex((c) => c.card_key === cardKey);
  const swap = idx + dir;
  if (idx < 0 || swap < 0 || swap >= row.cards.length) return boardRows;
  const tmp = row.cards[idx];
  row.cards[idx] = row.cards[swap];
  row.cards[swap] = tmp;
  return rows;
}

export function relocateCard(boardRows, fromSection, cardKey, toSection, toIndex) {
  const rows = boardRows.map((r) => ({
    ...r,
    cards: [...r.cards],
  }));
  const from = rows.find((r) => r.sectionKey === fromSection);
  const to = rows.find((r) => r.sectionKey === toSection);
  if (!from || !to) return boardRows;
  const idx = from.cards.findIndex((c) => c.card_key === cardKey);
  if (idx < 0) return boardRows;
  const [card] = from.cards.splice(idx, 1);
  let insertAt = toIndex;
  if (fromSection === toSection && insertAt > idx) insertAt -= 1;
  insertAt = Math.max(0, Math.min(insertAt, to.cards.length));
  to.cards.splice(insertAt, 0, { ...card, section_key: toSection });
  return rows;
}

export function relocateSection(sectionDefs, fromIndex, toIndex) {
  const next = [...sectionDefs];
  if (
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= next.length ||
    toIndex >= next.length ||
    fromIndex === toIndex
  ) {
    return sectionDefs;
  }
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next.map((s, i) => ({ ...s, sort_order: i * 10 }));
}
